import {
  type ClientContactRow,
} from '../../../shared/types';
import type { ResolvedProjectContact } from '../../../shared/clientContact';
import {
  contactOptionLabel,
} from '../../lib/projectDetailShared';
import { FieldRow } from '../FieldSheet';
import type { ProjectDetail, FieldEdit } from './types';
/** Project contact picker and overrides, matching the desktop rail. */
function ContactRows({
  project,
  clientContacts,
  resolvedContact,
  transientCustom,
  setTransientCustom,
  openField,
  openPicker,
  patch,
}: {
  project: ProjectDetail;
  clientContacts: ClientContactRow[];
  resolvedContact: ResolvedProjectContact;
  transientCustom: boolean;
  setTransientCustom: (custom: boolean) => void;
  openField: (edit: FieldEdit) => void;
  openPicker: () => void;
  patch: (fields: Record<string, unknown>) => Promise<void>;
}) {
  const hasRegister = clientContacts.length > 0;
  const customMode =
    transientCustom ||
    (project.contact_id === null &&
      (project.contact_name !== null || project.contact_email !== null || project.contact_phone !== null));

  const saveCustom = async (
    field: 'contact_name' | 'contact_email' | 'contact_phone',
    value: string | null,
  ) => {
    await patch({ [field]: value });
    if (value !== null) setTransientCustom(false);
  };

  const editable = !hasRegister || customMode;
  const defaultContact = clientContacts.find((c) => c.is_default === 1) ?? null;
  const picked = project.contact_id
    ? (clientContacts.find((c) => c.id === project.contact_id) ?? null)
    : null;
  const selected = picked ?? (!customMode ? defaultContact : null);

  return (
    <>
      {hasRegister && (
        <FieldRow
          label="Contact"
          value={
            customMode
              ? 'Custom…'
              : selected
                ? contactOptionLabel(selected, selected.id === defaultContact?.id)
                : (resolvedContact.name ?? '')
          }
          empty={!customMode && !selected && !resolvedContact.name}
          onPress={openPicker}
        />
      )}
      {editable ? (
        <>
          {!hasRegister && (
            <FieldRow
              label="Contact"
              value={project.contact_name ?? project.client_contact_name ?? ''}
              empty={!project.contact_name && !project.client_contact_name}
              onPress={() =>
                openField({
                  title: 'Contact name',
                  context: project.name,
                  kind: 'text',
                  value: project.contact_name ?? '',
                  onSave: (raw) => void saveCustom('contact_name', raw.trim() || null),
                })
              }
            />
          )}
          {hasRegister && (
            <FieldRow
              label="Name"
              value={project.contact_name ?? ''}
              empty={!project.contact_name}
              onPress={() =>
                openField({
                  title: 'Contact name',
                  context: project.name,
                  kind: 'text',
                  value: project.contact_name ?? '',
                  onSave: (raw) => void saveCustom('contact_name', raw.trim() || null),
                })
              }
            />
          )}
          <FieldRow
            label="Email"
            mono
            tone="acc"
            value={project.contact_email ?? (hasRegister ? '' : (project.client_contact_email ?? ''))}
            empty={!project.contact_email && (hasRegister || !project.client_contact_email)}
            onPress={() =>
              openField({
                title: 'Contact email',
                context: project.name,
                kind: 'email',
                value: project.contact_email ?? '',
                onSave: (raw) => void saveCustom('contact_email', raw.trim() || null),
              })
            }
          />
          <FieldRow
            label="Phone"
            mono
            value={project.contact_phone ?? (hasRegister ? '' : (project.client_contact_phone ?? ''))}
            empty={!project.contact_phone && (hasRegister || !project.client_contact_phone)}
            onPress={() =>
              openField({
                title: 'Contact phone',
                context: project.name,
                kind: 'tel',
                value: project.contact_phone ?? '',
                onSave: (raw) => void saveCustom('contact_phone', raw.trim() || null),
              })
            }
          />
        </>
      ) : (
        <>
          <div className="fieldrow">
            <span className="fieldrow-k">Email</span>
            {resolvedContact.email ? (
              <a className="fieldrow-v mono acc" href={`mailto:${resolvedContact.email}`}>
                {resolvedContact.email}
              </a>
            ) : (
              <span className="fieldrow-v empty">—</span>
            )}
          </div>
          <div className="fieldrow">
            <span className="fieldrow-k">Phone</span>
            {resolvedContact.phone ? (
              <a className="fieldrow-v mono acc" href={`tel:${resolvedContact.phone}`}>
                {resolvedContact.phone}
              </a>
            ) : (
              <span className="fieldrow-v empty">—</span>
            )}
          </div>
        </>
      )}
    </>
  );
}

export { ContactRows };
